import { Routes } from '@angular/router';
import { AppLayoutComponent } from '@daisi/components/layout/app-layout.component';
import { ndwAuthGuard } from '@ndwnu/auth';
import {
  adminAuthGuardConfig,
  dashboardAuthGuardConfig,
  defaultAuthGuardConfig,
  incidentsAuthGuardConfig,
  roadworksAuthGuardConfig,
  trafficAuthGuardConfig,
  userAdminAuthGuardConfig,
} from '@daisi/auth.config';
import {
  mapSnapshotPath,
  overviewPath,
  roadworksPath,
  settingsPath,
  statisticsPath,
  timelineSnapshotPath,
  trafficPath,
  userPath,
} from '@daisi/app.paths';

export const routes: Routes = [
  {
    // Deliberately unguarded: this page is loaded by headless Chromium to turn into a picture for
    // the notification mail. There is no Keycloak session, and the page itself sends no
    // credentials. The backend hands Gotenberg a service-account token as an `extraHttpHeaders`
    // entry, so Chromium attaches the `Authorization` header to this navigation and to the page's
    // own XHR. The token therefore never appears in a URL, and must not be put back into one: it
    // would end up in the ingress, nginx and Gotenberg access logs. See
    // timeline-snapshot-page.component.ts.
    //
    // Outside the app layout, so the picture holds only the page itself, and none of the layout's
    // navigation, banners and release-notes popup gets loaded.
    path: `${timelineSnapshotPath}/:incidentId`,
    loadComponent: () =>
      import('@pages/timeline-snapshot-page/timeline-snapshot-page.component').then(
        (c) => c.TimelineSnapshotPageComponent,
      ),
  },
  {
    // Unguarded and outside the app layout for the same reasons as the timeline snapshot above.
    path: `${mapSnapshotPath}/:incidentId`,
    loadComponent: () =>
      import('@pages/map-snapshot-page/map-snapshot-page.component').then(
        (c) => c.MapSnapshotPageComponent,
      ),
  },
  {
    path: '',
    component: AppLayoutComponent,
    children: [
      { path: '', pathMatch: 'full', redirectTo: overviewPath },
      {
        path: overviewPath,
        loadComponent: () =>
          import('@pages/overview-page/overview-page.component').then(
            (c) => c.OverviewPageComponent,
          ),
        canActivate: [ndwAuthGuard(incidentsAuthGuardConfig)],
      },
      {
        path: trafficPath,
        loadComponent: () =>
          import('@pages/traffic-page/traffic-page.component').then((c) => c.TrafficPageComponent),
        canActivate: [ndwAuthGuard(trafficAuthGuardConfig)],
      },
      {
        path: roadworksPath,
        loadComponent: () =>
          import('@pages/roadworks-page/roadworks-page.component').then(
            (c) => c.RoadworksPageComponent,
          ),
        canActivate: [ndwAuthGuard(roadworksAuthGuardConfig)],
      },
      {
        path: statisticsPath,
        loadComponent: () =>
          import('@pages/statistics-page/statistics-page.component').then(
            (c) => c.StatisticsPageComponent,
          ),
        canActivate: [ndwAuthGuard(dashboardAuthGuardConfig)],
      },
      {
        path: settingsPath,
        loadComponent: () =>
          import('@pages/settings-page/settings-page.component').then(
            (c) => c.SettingsPageComponent,
          ),
        canActivate: [ndwAuthGuard(defaultAuthGuardConfig)],
      },
      {
        path: 'release-notes',
        loadComponent: () =>
          import('@pages/release-notes/pages/list/release-notes-overview.component').then(
            (c) => c.ReleaseNotesOverviewComponent,
          ),
        canActivate: [ndwAuthGuard(adminAuthGuardConfig)],
      },
      {
        path: 'infoberichten',
        loadComponent: () =>
          import('@daisi/pages/info-messages/pages/list/info-messages-list-page.component').then(
            (c) => c.InfoMessagesListPageComponent,
          ),
        canActivate: [ndwAuthGuard(adminAuthGuardConfig)],
      },
      {
        path: 'updates',
        loadComponent: () =>
          import('@pages/release-notes/pages/list/release-notes-overview.component').then(
            (c) => c.ReleaseNotesOverviewComponent,
          ),
        data: {
          isUpdatePage: true,
        },
        canActivate: [ndwAuthGuard(defaultAuthGuardConfig)],
      },
      {
        path: userPath,
        loadComponent: () =>
          import('@pages/users/pages/user-list-page/user-list-page.component').then(
            (c) => c.UserListPageComponent,
          ),
        data: {
          editAllowed: false,
        },
        canActivate: [ndwAuthGuard(userAdminAuthGuardConfig)],
      },
      {
        path: '**',
        loadComponent: () =>
          import('@daisi/pages/not-found/not-found.component').then((c) => c.NotFoundComponent),
      },
    ],
  },
];
